import axios from 'axios';
import { auth } from '~/util';

/**
 * @typedef {Object} RequestOptions
 * @property {boolean} [authHeader=true] - Send Authorization header with token in request
 * @property {Object} [headers={}] - Other headers to include in the request
 * @property {boolean} [rawRequest=false] - Return the original response object instead of just the payload
 */

/**
 * Make an HTTP GET request with optional substitution
 * @param {string} endpoint - URL to send a GET request to
 * @param {Object} [params] - Key:Value pairs to substitute in the URL
 * @param {RequestOptions} [options] - Request options
 */
export const get = (
    endpoint,
    params = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
   
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let target = endpoint.replace(/{([^}]+)}/g, (_, key) => params[key]);
    let config = { headers, ...options };

    if (authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.get(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => {
            const { data, status } = ex.response;
            
            if(status == 401) {
                window.location = '/un-authorized';
            }

            return {
                ...data
            };
        });
};

export const get2 = (
    endpoint,
    params = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
   
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError) return tokenExpiredError;
    }

    const query = Object.keys(params).reduce((previous, current)=>{
        return previous += `${current}=${params[current]}&`;
    }, '');
    let target = `${endpoint}?${query}`.trimEnd('?');
    let config = { headers, ...options };

    if(authHeader) 
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
        
    return axios.get(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => {
            const { data, status } = ex.response;
            
            if(status == 401) {
                window.location = '/un-authorized';
            }

            return {
                ...data
            };
        });
};

/**
 * Make an HTTP POST request
 * @param {string} endpoint - URL to send a POST request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const post = (
    endpoint,
    data = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }
    
    return axios.post(endpoint, data, config)
        .then(response => {
            return rawRequest ? response : response.data;
        })
        .catch(ex => {
            return ex.response 
                ? { ...ex.response.data }
                : { ...ex };
        });
};

/**
 * Make an HTTP DELETE request with optional substitution
 * @param {string} endpoint - URL to send a DELETE request to
 * @param {Object} [params] - Key:Value pairs to substitute in the URL
 * @param {RequestOptions} [options] - Request options
 */
export const del = (
    endpoint,
    params = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let target = endpoint.replace(/{(.*)}/g, (_, key) => params[key]);
    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }

    return axios.delete(target, config)
        .then(response => rawRequest ? response : response.data)
        .catch(ex => ({ errored: true, error: (ex.response ? ex.response.data.error : ex.message) || ex }));
};

/**
 * Make an HTTP PUT request
 * @param {string} endpoint - URL to send a PUT request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const put = (
    endpoint,
    data = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }
    
    return axios.put(endpoint, data, config)
        .then(response => {
            return rawRequest ? response : response.data;
        })
        .catch(ex => { 
            return {
                ...ex.response.data
            };
        });
};

/**
 * Make an HTTP PATCH request
 * @param {string} endpoint - URL to send a PUT request to
 * @param {Object} [data] - Body of the request
 * @param {RequestOptions} [options] - Request options
 */
export const patch = (
    endpoint,
    data = {},
    { authHeader = true, headers = {}, rawRequest = false, ...options } = {}
) => {
    if(authHeader) {
        const tokenExpiredError = auth.isTokenExpired();
        if (tokenExpiredError)
            return tokenExpiredError;
    }

    let config = { headers, ...options };

    if(authHeader) {
        config.headers.Authorization = `Bearer ${auth.getToken()}`;
    }
    
    return axios.patch(endpoint, data, config)
        .then(response => {
            return rawRequest ? response : response.data;
        })
        .catch(ex => { 
            return {
                ...ex.response.data
            };
        });
};

export default {
    get2,
    get,
    post,
    del,
    put,
    patch
};
